import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../utils/api';
import { AddPermission, PermissionList, AddModulePermission } from '../dtos/permission';
import { accessToken } from '../utils/helper';
import { Menu } from '../dtos/module';

interface RoleState {
  permissionList:
    | { data: PermissionList }
    | {
        data: {
          menuPermissions: { roleId: number; menuId: number }[];
          rolePermissions: { roleId: number; submoduleId: number }[];
        };
      };
  loading: boolean;
  error: string | null;
  addPermissionResponse: { message: string };
  addPermissionError: string | null;
  modulePermissionList: { data: { roleId: number; moduleId: number }[] };
  addModulePermissionResponse: { message: string };
  subModuleList: { data: { id: number; name: string; subModules: { roleKey: string }[] } };
  menuName: string;
  selectedMenuList: { data: Menu[] };
}

const initialState: RoleState = {
  permissionList: { data: { menuPermissions: [], rolePermissions: [] } },
  addPermissionResponse: { message: '' },
  loading: false,
  error: null,
  addPermissionError: null,
  modulePermissionList: { data: [{ roleId: 0, moduleId: 0 }] },
  addModulePermissionResponse: { message: '' },
  subModuleList: { data: { id: 0, name: '', subModules: [{ roleKey: '' }] } },
  menuName: '',
  selectedMenuList: { data: [] },
};

export const getAllPermissionTrigger = createAsyncThunk('/permissions', async () => {
  try {
    const response = await api.get<{ data: PermissionList }>('/permissions', accessToken());
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: { menuPermissions: [], rolePermissions: [] } };
  }
});

export const addPermissionTrigger = createAsyncThunk(
  '/permission/add',
  async (request: AddPermission) => {
    try {
      const response = await api.post<{ message: string }>(
        `/permissions/add`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '' };
    }
  },
);

export const addModulePermissionTrigger = createAsyncThunk(
  '/module-permission/add',
  async (request: AddModulePermission) => {
    try {
      const response = await api.post<{ message: string }>(
        `/module-permissions/add`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      console.log(error);
      return { message: '' };
    }
  },
);

export const getAllModulePermissionTrigger = createAsyncThunk('/module-permissions', async () => {
  try {
    const response = await api.get<{ data: { roleId: number; moduleId: number }[] }>(
      '/module-permissions',
      accessToken(),
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: [{ roleId: 0, moduleId: 0 }] };
  }
});

export const getAllPermissionByRoleTrigger = createAsyncThunk(
  '/permissions/:id',
  async (id: number) => {
    try {
      const response = await api.get<{
        data: { id: number; name: string; subModules: { roleKey: string }[] };
      }>('/permissions/' + id, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: { id: 0, name: '', subModules: [{ roleKey: '' }] } };
    }
  },
);

export const getMenuByModule = createAsyncThunk('/get/menus/:slug', async (slug: string) => {
  try {
    const response = await api.get<{ data: Menu[] }>('/menus/list/' + slug, accessToken());
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: [] };
  }
});

// Create slice
const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    getMenu(state, action: PayloadAction<string>) {
      state.menuName = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPermissionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPermissionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.permissionList = action.payload;
      })
      .addCase(getAllPermissionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(addPermissionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPermissionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addPermissionResponse = action.payload;
      })
      .addCase(addPermissionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addPermissionError = action.error.message || 'Failed to load posts';
      })
      .addCase(addModulePermissionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addModulePermissionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addModulePermissionResponse = action.payload;
      })
      .addCase(addModulePermissionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(getAllModulePermissionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllModulePermissionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.modulePermissionList = action.payload;
      })
      .addCase(getAllModulePermissionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addPermissionError = action.error.message || 'Failed to load posts';
      })
      .addCase(getAllPermissionByRoleTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPermissionByRoleTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.subModuleList = action.payload;
      })
      .addCase(getAllPermissionByRoleTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addPermissionError = action.error.message || 'Failed to load posts';
      })
      .addCase(getMenuByModule.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMenuByModule.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMenuList = action.payload;
      })
      .addCase(getMenuByModule.rejected, (state, action) => {
        state.loading = false;
        state.addPermissionError = action.error.message || 'Failed to load posts';
      });
  },
});

export const { getMenu } = permissionSlice.actions;

export default permissionSlice.reducer;
